<template>
  <div v-if="enabled" id="app" class="wishlist-details">
    <div class="wishlist-details__wrapper">
      <CdrGrid justify="between" class="wishlist-details__navigation">
        <div>
          <BreadCrumbComponent
            v-if="userLoggedIn && ownedList && breadcrumbModel && breadcrumbModel.enabled"
            :breadcrumb-data="breadcrumbModel"
          />
        </div>
        <div>
          <PrintShareButtonsComponent :list-name="name" :is-list-public="isPublic" />
        </div>
      </CdrGrid>

      <CdrGrid vertical="vertical" class="wishlist-details__grid">
        <div>
          <div class="wishlist-details__card">
            <CdrText v-if="userLoggedIn && ownedList && isPrimary" class="wishlist-details__type" tag="span">
              Primary wish list
            </CdrText>

            <div class="wishlist-details__heading">
              <CdrText class="wishlist-details__title" data-ui="list_name_detail">
                {{ name }}
              </CdrText>

              <CdrText class="wishlist-details__list-type">
                <span>{{ type }}</span> wish list
              </CdrText>

              <CdrButton
                v-if="userLoggedIn && ownedList && editLink.enabled && !isEditListOpen"
                class="wishlist-details__edit-link"
                data-ui="wishlist-editLink"
                modifier="secondary"
                size="small"
                @click="openEditListComponent"
              >
                <template #icon-left>
                  <IconEdit inherit-color />
                </template>
                {{ editLink.text }}
              </CdrButton>
            </div>
          </div>

          <transition name="fade">
            <EditAndDeleteWishlistComponent
              v-if="userLoggedIn && ownedList && isEditListOpen"
              :list-id="wishlistDetails.id"
              :list-name="name"
              :public-list="isPublic"
              :is-primary="isPrimary"
              :field-label="fieldLabel"
              :save-button="saveButton"
              :cancel-button="cancelButton"
              :delete-link="deleteLink"
              :error-messages="errorMessages"
              :list-type="type"
              :disclaimer="privacyPolicyMessage"
              @close="closeEditListComponent"
              @list-name-updated="updateListName"
              @list-type-updated="updateListType"
            />
          </transition>
        </div>
      </CdrGrid>
    </div>

    <WishlistDetailComponent
      :user-logged-in="userLoggedIn"
      :owned-list="ownedList"
      :wishlist-items-lists="wishlistDetails"
      :user-wishlists="userWishlist"
      :categories-list="categoriesList"
      :new-global-nav="newGlobalNav"
    />
  </div>
</template>

<script>
import { CdrGrid, CdrButton, CdrText, IconEdit } from '@rei/cedar';
import { mapWritableState, mapActions } from 'pinia';
import BreadCrumbComponent from '../../components/common/BreadCrumbComponent.vue';
import EditAndDeleteWishlistComponent from '../../components/list-details/EditAndDeleteWishlistComponent.vue';
import WishlistDetailComponent from '../../components/list-details/WishlistDetailComponent.vue';
import PrintShareButtonsComponent from '../../components/list-details/PrintShareButtonsComponent.vue';
import metrics from '../../utils/metrics';
import { useWishlistStore } from '../../stores';

export default {
  name: 'ListDetailsPageComponent',
  components: {
    CdrGrid,
    CdrText,
    CdrButton,
    IconEdit,
    BreadCrumbComponent,
    WishlistDetailComponent,
    EditAndDeleteWishlistComponent,
    PrintShareButtonsComponent,
  },

  props: {
    pageTags: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    breadcrumbModel: {
      type: Object,
      default: () => {},
    },
    wishlistDetails: {
      type: Object,
      default: () => {},
    },
    userWishlist: {
      type: Array,
      required: true,
    },
    enabled: {
      type: Boolean,
      required: true,
    },
    userLoggedIn: {
      type: Boolean,
      required: true,
    },
    ownedList: {
      type: Boolean,
      required: true,
    },
    fieldLabel: {
      type: String,
      required: true,
    },
    publicSelector: {
      type: String,
      required: true,
    },
    privateSelector: {
      type: String,
      required: true,
    },
    saveButton: {
      type: Object,
      default: () => {},
    },
    cancelButton: {
      type: Object,
      default: () => {},
    },
    editLink: {
      type: Object,
      default: () => {},
    },
    deleteLink: {
      type: Object,
      default: () => {},
    },
    privacyPolicyMessage: {
      type: String,
      required: true,
    },
    categoriesList: {
      type: Array,
      required: false,
      default: () => [],
    },
    errorMessages: {
      type: Object,
      default: () => {},
    },
    newGlobalNav: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      name: this.wishlistDetails.name,
      isPublic: this.wishlistDetails.public,
      type: '',
      isEditListOpen: false,
    };
  },
  computed: {
    ...mapWritableState(useWishlistStore, ['wishlist', 'availableChanges']),
    isPrimary() {
      const {
        wishlistDetails: { type },
      } = this;
      return type === 'WishList';
    },
  },
  created() {
    this.setWishlist(this.wishlistDetails);
    this.setAvailableChanges(this.userLoggedIn && this.ownedList);
  },
  mounted() {
    const metricsPayload = {
      pageDescription: this.userLoggedIn && this.ownedList ? 'list' : 'public view',
      wishListPrivacyStatus: this.isPublic ? 'public' : 'private',
      numWishListItems: this.wishlistDetails.entries.length,
      wishListId: this.wishlistDetails.id,
    };
    this.setListType();
    const { currentPageTags } = metrics.pageLoad(this.pageTags, metricsPayload);
    this.$emit('update:pageTags', currentPageTags);
  },
  methods: {
    ...mapActions(useWishlistStore, ['updateList']),
    setWishlist(list) {
      this.wishlist = list;
    },
    setAvailableChanges(available) {
      this.availableChanges = available;
    },
    setListType() {
      this.type = this.isPublic ? 'public' : 'private';
    },
    updateListType(value) {
      this.isPublic = value;
      this.setListType();
    },
    updateListName(value) {
      this.name = value;
    },
    openEditListComponent() {
      metrics.metricsClick(this.editLink.text);
      this.isEditListOpen = true;
    },
    closeEditListComponent() {
      this.isEditListOpen = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';

.wishlist-details {
  @include cdr-container;

  &__wrapper {
    padding-top: $cdr-space-one-and-a-half-x;
  }

  &__navigation {
    grid-template-columns: 6fr 6fr;
  }

  &__type {
    font-size: $cdr-text-heading-sans-200-size;
  }

  &__grid {
    @include cdr-sm-mq-only {
      grid-template-columns: 8fr 4fr;
    }
    @include cdr-md-mq-up {
      grid-template-columns: 6fr 6fr;
    }
  }

  &__card {
    margin-top: $cdr-space-one-x;
  }

  &__heading {
    p {
      margin: 0;
    }
  }

  &__title {
    @include cdr-text-utility-serif-600;
  }

  &__edit-link {
    margin-bottom: $cdr-space-one-and-a-half-x;
    margin-top: $cdr-space-one-x;
  }

  &__list-type {
    @include cdr-text-utility-sans-300;
    span {
      text-transform: capitalize;
    }
  }
}

.copied input {
  padding-left: $cdr-space-two-x;
}

.updated input {
  box-shadow: 1px 1px 4px 2px rgba(0, 0, 0, 0.59);
  transition: all 0.6s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.leave input {
  box-shadow: inset 0 0 0 0.1rem #928b80;
  transition: all 0.6s cubic-bezier(0.165, 0.84, 0.44, 1);
}

// Transitions styles
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@media screen and (min-width: $cdr-breakpoint-lg) {
  .wishlist-details {
    padding-left: $cdr-space-two-x;
    padding-right: $cdr-space-two-x;
  }
}

@media print {
  #headerWrapper,
  #minicart-wrapper,
  body #gladlyChat_container,
  .wishlists__type,
  .wishlists-breadcrumb,
  .print-share-links,
  .wishlist-details__edit-link,
  .update-wishlist__type,
  #page-footer,
  .head3 .product__link,
  .product__note-button,
  .product__button,
  .product__note,
  .list-item__quantity-actions,
  .list-item__actions-select,
  .product__select {
    display: none !important;
  }
  .product__quantity {
    display: block;
  }
  #wishlist {
    clear: both;
  }
}
</style>
